import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { useDashboardStore } from '../store/useDashboardStore';
import useDashboardApi from './useDashboardApi';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { getDateRangeFromPreset } from '../types/dashboard.types';
import type { DashboardSession, LoadingState } from '../types/dashboard.types';
import type {
  DashboardOverview,
  DashboardOverviewKpis,
  TimeSeriesPoint,
  DispositionSlice,
  SdrRanking,
  FunnelStage,
  AgentRanking,
  OverviewGranularity,
  OverviewQueryParams,
} from '@types';


export interface RecentSession {
  id: string;
  shortId: string;
  sdrId: string;
  sdrName?: string;
  listNames: string;
  type: string;
  organizationName?: string;
  totalCalls: number;
  connectRate: number;
  appointmentsSet: number;
  status: string;
  startDate: string | null;
}

interface KpisApiResponse {
  kpis: DashboardOverviewKpis;
  callsOverTime: TimeSeriesPoint[];
  dispositionBreakdown: DispositionSlice[];
}

interface LeaderboardApiResponse {
  sdrLeaderboard: SdrRanking[];
  pipelineFunnel: FunnelStage[];
  agentLeaderboard: AgentRanking[];
}

// The overview endpoints are typed as `unknown` over the wire. Validate the
// shape before using it, so a backend change degrades to an error state instead
// of crashing KpiCard/charts on an undefined field.
function isKpisApiResponse (data: unknown): data is KpisApiResponse {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.kpis === 'object' && d.kpis !== null &&
    Array.isArray(d.callsOverTime) &&
    Array.isArray(d.dispositionBreakdown)
  );
}

function isLeaderboardApiResponse (data: unknown): data is LeaderboardApiResponse {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return Array.isArray(d.sdrLeaderboard) && Array.isArray(d.pipelineFunnel);
}

export interface UseDashboardOverviewVMReturn {
  overview: DashboardOverview | null;
  recentSessions: RecentSession[];
  loadingState: LoadingState;
  /** A refetch over an already-visible dashboard — never the first load. */
  isRefreshing: boolean;
  granularity: OverviewGranularity;
  prevPeriod: { from: Date; to: Date } | null;
  setGranularity: (g: OverviewGranularity) => void;
  refresh: () => Promise<void>;
}

/**
 * Mirrors backend OverviewService.calculatePreviousPeriod — returns the
 * immediately-prior window of the same length as the current selection.
 */
function calculatePreviousPeriod (from: Date, to: Date): { from: Date; to: Date } {
  const durationMs = to.getTime() - from.getTime();
  const prevTo = new Date(from.getTime() - 1);
  const prevFrom = new Date(prevTo.getTime() - durationMs);
  return { from: prevFrom, to: prevTo };
}

const RECENT_SESSIONS_LIMIT = 4;

export default function useDashboardOverviewVM (): UseDashboardOverviewVMReturn {
  const { currentUser } = useSessionStore();
  const dashboardApi = useDashboardApi();
  const {
    filters,
    overviewGranularity,
    setOverviewGranularity,
  } = useDashboardStore();

  const currentUserId = currentUser?.userId ?? '';
  /**
   * The zone the backend cuts day buckets on. Falls back to the browser's own
   * when the profile has none, which is closer to right than UTC for everyone
   * except a viewer actually in UTC.
   */
  const timezone = currentUser?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('LOADING');
  const [prevPeriod, setPrevPeriod] = useState<{ from: Date; to: Date } | null>(null);
  // Refreshing something already on screen, as opposed to the first load. Keeps
  // the page rendered instead of swapping every card for a skeleton.
  const hasLoadedOnce = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Cache org/sdr name maps to avoid refetching every refresh
  const orgNameMapRef = useRef<Map<string, string>>(new Map());
  const sdrNameMapRef = useRef<Map<string, string>>(new Map());

  const buildEffectiveFilters = useCallback(() => {
    // `statuses` belongs to the sessions tab, not here. Left in, the overview's
    // Recent Sessions table would come back filtered to active sessions — which
    // is what put a June session in a "Last 7 days" overview — and the KPIs
    // above it would describe a different set than the rows below.
    const effectiveFilters = { ...filters, statuses: [] };
    if (effectiveFilters.dateRange?.from == null && effectiveFilters.dateRange?.preset !== 'custom') {
      const dateRange = getDateRangeFromPreset(effectiveFilters.dateRange?.preset ?? 'last7days');
      effectiveFilters.dateRange = {
        ...effectiveFilters.dateRange,
        from: dateRange.from,
        to: dateRange.to,
      };
    }
    return effectiveFilters;
  }, [filters]);

  const enrichSessions = useCallback(async (
    sessions: DashboardSession[],
  ): Promise<RecentSession[]> => {
    if (sessions.length === 0) {
      return [];
    }

    // Fetch missing org names
    const missingOrgIds = [
      ...new Set(
        sessions
          .map((s) => s.organizationId)
          .filter((id) => id !== '' && !orgNameMapRef.current.has(id)),
      ),
    ];

    if (missingOrgIds.length > 0) {
      const orgsResponse = await dashboardApi.getOrganizationsAsync();
      if (orgsResponse.success && orgsResponse.data !== undefined) {
        orgsResponse.data.forEach((org) => {
          orgNameMapRef.current.set(org.id, org.name);
        });
      }
    }

    // Fetch missing SDR names
    const missingSdrIds = [
      ...new Set(
        sessions
          .map((s) => s.sdrId)
          .filter((id) => id !== '' && !sdrNameMapRef.current.has(id)),
      ),
    ];

    if (missingSdrIds.length > 0) {
      const sdrsResponse = await dashboardApi.getSdrsAsync(
        currentUserId,
        missingSdrIds,
        null,
      );
      if (sdrsResponse.success && sdrsResponse.data.length > 0) {
        sdrsResponse.data.forEach((sdr) => {
          sdrNameMapRef.current.set(sdr.id, sdr.name);
        });
      }
    }

    return sessions.map((session) => ({
      id: session.id,
      shortId: session.shortId ?? session.id.slice(-4),
      sdrId: session.sdrId,
      sdrName: sdrNameMapRef.current.get(session.sdrId) ?? session.sdrName,
      listNames: session.listNames,
      type: session.type,
      organizationName: orgNameMapRef.current.get(session.organizationId) ?? session.organizationName,
      totalCalls: session.totalCalls ?? 0,
      connectRate: session.connectRate ?? 0,
      appointmentsSet: session.appointmentsSet ?? 0,
      status: session.status,
      startDate: typeof session.startAtSession === 'string'
        ? session.startAtSession
        : (session.startAtSession as { $date: string })?.$date ?? null,
    }));
  }, [currentUserId, dashboardApi]);

  const refresh = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }

    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
    } else {
      setLoadingState('LOADING');
    }

    try {
      const effectiveFilters = buildEffectiveFilters();

      const dateFrom = effectiveFilters.dateRange.from instanceof Date
        ? effectiveFilters.dateRange.from
        : new Date(effectiveFilters.dateRange.from ?? new Date());
      const dateTo = effectiveFilters.dateRange.to instanceof Date
        ? effectiveFilters.dateRange.to
        : new Date(effectiveFilters.dateRange.to ?? new Date());

      setPrevPeriod(calculatePreviousPeriod(dateFrom, dateTo));

      const queryParams: OverviewQueryParams = {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        granularity: overviewGranularity,
        // Required: the backend scopes the results to what this user may see.
        userId: currentUserId,
        timezone,
      };

      if (effectiveFilters.orgIds.length > 0) {
        queryParams.orgId = effectiveFilters.orgIds[0];
      }

      if (effectiveFilters.sdrIds.length > 0) {
        queryParams.sdrId = effectiveFilters.sdrIds[0];
      }

      if (effectiveFilters.listTypes.length > 0) {
        queryParams.listTypes = effectiveFilters.listTypes;
      }

      if (effectiveFilters.searchQuery !== '') {
        queryParams.searchQuery = effectiveFilters.searchQuery;
      }

      // Fetch all 3 in parallel: KPIs, leaderboard, and recent sessions
      const [kpisResponse, leaderboardResponse, sessionsResponse] = await Promise.all([
        dashboardApi.getOverviewKpisAsync(queryParams),
        dashboardApi.getOverviewLeaderboardAsync(queryParams),
        dashboardApi.getSessionsAsync(
          currentUserId,
          effectiveFilters,
          1,
          RECENT_SESSIONS_LIMIT,
        ),
      ]);

      // ── Overview (KPIs + charts) ──
      let kpisData: KpisApiResponse | null = null;
      let leaderboardData: LeaderboardApiResponse | null = null;

      if (kpisResponse.success && isKpisApiResponse(kpisResponse.data)) {
        kpisData = kpisResponse.data;
      }

      if (leaderboardResponse.success && isLeaderboardApiResponse(leaderboardResponse.data)) {
        leaderboardData = leaderboardResponse.data;
      }

      if (kpisData !== null && leaderboardData !== null) {
        setOverview({
          kpis: kpisData.kpis,
          callsOverTime: kpisData.callsOverTime,
          dispositionBreakdown: kpisData.dispositionBreakdown,
          sdrLeaderboard: leaderboardData.sdrLeaderboard,
          pipelineFunnel: leaderboardData.pipelineFunnel,
          // Optional on the wire so an older backend degrades to an empty
          // agent card instead of crashing the whole overview.
          agentLeaderboard: leaderboardData.agentLeaderboard ?? [],
        });

        void logInfo('Dashboard overview loaded from API.', 'DashboardOverviewVM.refresh', {
          totalCalls: kpisData.kpis?.totalCalls?.value,
          sdrCount: leaderboardData.sdrLeaderboard?.length,
        });
      } else {
        setOverview(null);

        void logError(
          'Failed to load dashboard overview.',
          'DashboardOverviewVM.refresh',
          {
            kpisSuccess: kpisResponse.success,
            kpisError: !kpisResponse.success ? kpisResponse.error : undefined,
            leaderboardSuccess: leaderboardResponse.success,
            leaderboardError: !leaderboardResponse.success ? leaderboardResponse.error : undefined,
          },
          true,
        );
      }

      // ── Recent Sessions (real endpoint) ──
      if (sessionsResponse.success && sessionsResponse.data?.data != null) {
        const enriched = await enrichSessions(sessionsResponse.data.data);
        setRecentSessions(enriched);
      } else {
        setRecentSessions([]);
      }

      setLoadingState('SUCCESS');
    } catch (error) {
      setOverview(null);
      setRecentSessions([]);
      setLoadingState('ERROR');

      void logError(
        'Failed to load dashboard overview.',
        'DashboardOverviewVM.refresh',
        { error },
        true,
      );
    } finally {
      hasLoadedOnce.current = true;
      setIsRefreshing(false);
    }
  }, [currentUserId, filters, overviewGranularity, timezone, dashboardApi, buildEffectiveFilters, enrichSessions]);

  const setGranularity = useCallback((g: OverviewGranularity) => {
    setOverviewGranularity(g);
  }, [setOverviewGranularity]);

  /**
   * Daily / Weekly / Monthly changes ONLY the time series. Refetching the
   * leaderboard and the recent sessions too — and blanking every card while it
   * ran — is what made switching tabs reload the whole page.
   */
  const refreshTimeSeries = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }

    setIsRefreshing(true);
    try {
      const effectiveFilters = buildEffectiveFilters();
      const dateFrom = effectiveFilters.dateRange.from instanceof Date
        ? effectiveFilters.dateRange.from
        : new Date(effectiveFilters.dateRange.from ?? new Date());
      const dateTo = effectiveFilters.dateRange.to instanceof Date
        ? effectiveFilters.dateRange.to
        : new Date(effectiveFilters.dateRange.to ?? new Date());

      const queryParams: OverviewQueryParams = {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        granularity: overviewGranularity,
        userId: currentUserId,
        timezone,
      };

      if (effectiveFilters.orgIds.length > 0) {
        queryParams.orgId = effectiveFilters.orgIds[0];
      }

      if (effectiveFilters.sdrIds.length > 0) {
        queryParams.sdrId = effectiveFilters.sdrIds[0];
      }

      if (effectiveFilters.listTypes.length > 0) {
        queryParams.listTypes = effectiveFilters.listTypes;
      }

      if (effectiveFilters.searchQuery !== '') {
        queryParams.searchQuery = effectiveFilters.searchQuery;
      }

      const response = await dashboardApi.getOverviewKpisAsync(queryParams);

      if (response.success && isKpisApiResponse(response.data)) {
        const data = response.data;

        // Merge, never replace: the leaderboard and the funnel come from the
        // other endpoint and must survive a granularity change untouched.
        setOverview((current) => (current === null
          ? current
          : {
            ...current,
            kpis: data.kpis,
            callsOverTime: data.callsOverTime,
            dispositionBreakdown: data.dispositionBreakdown,
          }));
      }
    } catch (error) {
      void logError(
        'Failed to reload the calls-over-time series.',
        'DashboardOverviewVM.refreshTimeSeries',
        { error },
        false,
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [currentUserId, overviewGranularity, timezone, dashboardApi, buildEffectiveFilters]);

  useEffect(() => {
    if (currentUserId !== '') {
      void refresh();
    }
  // Granularity is deliberately NOT here — it has its own lighter effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, filters]);

  useEffect(() => {
    // Skip until the first full load has happened, or this fires alongside it
    // and issues the same request twice on mount.
    if (!hasLoadedOnce.current) {
      return;
    }
    void refreshTimeSeries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewGranularity]);

  return {
    overview,
    recentSessions,
    loadingState,
    isRefreshing,
    granularity: overviewGranularity,
    prevPeriod,
    setGranularity,
    refresh,
  };
}
